/**
 * 商品库条目：主图 / 详情图 URL 解析与字段规范化（列表页、详情页共用）。
 * 主图仅 1 张；其余候选图并入详情图列。
 * 方案三：若 raw/imageAssets 存在，按 role 分流（主图=composite|detail 顶上，详情列=detail + 其余主图候选）。
 */

import i18n from '@/i18n';
import {
  collectCarouselUrlsFromImageAssets,
  collectDetailUrlsFromImageAssets,
  collectPrintUrlsFromImageAssets,
  IMAGE_ASSET_ROLES,
  normalizeImageAssetList,
} from './imageAssetModel';
import { getListingPreferredOverrides, resolveListingItemDisplayTitle } from './preferDownstreamListingAssets';
import { resolveListingColorSlots, detailUrlsFromListingColorSlot } from './platformListingProductMeta';
import {
  resolveListingBridgeSourceLabelFromItem,
} from './listingBridgeSourceLabel';
import { TEMU_MAX_DETAIL_IMAGES as TEMU_SKC_DETAIL_MAX } from '@/temu-platform/utils/temuProductImage';

/** 商品库列表「详情图」列最多展示张数（详情页可继续上传，不受此限） */
export const MAX_LISTING_DETAIL_IMAGES = 5;

/** 商品库列表「印花图」列最多展示张数（详情页可继续上传，不受此限） */
export const MAX_LISTING_PRINT_IMAGES = 5;

/** SHEIN SKC 细节图上限（与 validateSheinPublishForm / SpecSupplySection 一致） */
export const SHEIN_MAX_DETAIL_IMAGES = 10;

/** TEMU SKC 细节图上限（主图+细节≤10；与 SpecSupplySection / validateTemuPublishForm 一致） */
export const TEMU_MAX_DETAIL_IMAGES = TEMU_SKC_DETAIL_MAX;

/** 商家货号拼接印花名后的建议上限（原 50，预留印花名空间） */
export const SHEIN_SUPPLIER_CODE_MAX_LEN = 100;

/** 商家 SKU 拼接印花名后的上限 */
export const SHEIN_SUPPLIER_SKU_MAX_LEN = 200;

export function uploadsPathKey(u) {
  const s = String(u || '').trim();
  const i = s.indexOf('/uploads/');
  if (i >= 0) return s.slice(i).split('?')[0];
  try {
    return new URL(s).pathname.split('?')[0];
  } catch {
    return s.split('?')[0];
  }
}

function urlsLooselySame(a, b) {
  if (!a || !b) return false;
  const sa = String(a).trim();
  const sb = String(b).trim();
  if (sa === sb) return true;
  return uploadsPathKey(sa) === uploadsPathKey(sb);
}

function uniqueUrlList(list, { excludeUrl = '', limit = 40 } = {}) {
  const out = [];
  const seen = new Set();
  for (const raw of list || []) {
    const u = String(raw || '').trim();
    if (!u) continue;
    if (excludeUrl && urlsLooselySame(u, excludeUrl)) continue;
    const key = uploadsPathKey(u) || u;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(u);
    if (out.length >= limit) break;
  }
  return out;
}

function parseUrlListField(value) {
  if (Array.isArray(value)) {
    return value.map((u) => String(u || '').trim()).filter(Boolean);
  }
  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed)
        ? parsed.map((u) => String(u || '').trim()).filter(Boolean)
        : [];
    } catch {
      return [];
    }
  }
  return [];
}

/** 从条目 / raw / snippet 提取数组字段（colorSlots、图片名等） */
function extractArrayField(row, camelKey, snakeKey) {
  const direct = row?.[camelKey] ?? row?.[snakeKey];
  if (Array.isArray(direct)) return direct;
  const rawObj = row?.raw && typeof row.raw === 'object' ? row.raw : null;
  if (rawObj) {
    const fromRaw = rawObj[camelKey] ?? rawObj[snakeKey];
    if (Array.isArray(fromRaw)) return fromRaw;
  }
  const snippet = String(row?.rawJsonSnippet || row?.raw_json_snippet || '').trim();
  if (!snippet) return [];
  try {
    const parsed = JSON.parse(snippet);
    const fromSnippet = parsed?.[camelKey] ?? parsed?.[snakeKey];
    if (Array.isArray(fromSnippet)) return fromSnippet;
  } catch {
    // snippet 可能被截断
  }
  return [];
}

/** 从 raw / rawJsonSnippet 提取 URL 列表（兼容旧后端未顶层回传 detailImageUrls） */
function extractUrlListFromRawFields(row, camelKey, snakeKey) {
  const rawObj = row?.raw && typeof row.raw === 'object' ? row.raw : null;
  if (rawObj) {
    const fromObj = parseUrlListField(rawObj[camelKey] ?? rawObj[snakeKey]);
    if (fromObj.length > 0) return fromObj;
  }
  const snippet = String(row?.rawJsonSnippet || row?.raw_json_snippet || '').trim();
  if (!snippet) return [];
  try {
    const parsed = JSON.parse(snippet);
    if (parsed && typeof parsed === 'object') {
      return parseUrlListField(parsed[camelKey] ?? parsed[snakeKey]);
    }
  } catch {
    // snippet 可能被截断，尝试正则兜底
    const key = camelKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const m = snippet.match(new RegExp(`"${key}"\\s*:\\s*(\\[[^\\]]*\\])`));
    if (m?.[1]) {
      try {
        return parseUrlListField(JSON.parse(m[1]));
      } catch {
        return [];
      }
    }
  }
  return [];
}

function isGoodsCompositeListingText(listingText) {
  const text = String(listingText || '');
  return text.includes('成品选中')
    || text.includes('来源：主图创建')
    || text.includes('来源：商品套图')
    || text.includes('来源：商品设计');
}

/** 主图/POD 套图入库：主图位展示首张成品/顶上详情图，其余进详情图列 */
export function isGoodsCompositeListingRow(row) {
  const pk = String(row?.batchPlatformKey || '').trim();
  if (pk === 'ipe-goods-composite') return true;
  const listingText = String(row?.listingText || '');
  return (
    String(row?.externalKey || '').includes('composite-') ||
    isGoodsCompositeListingText(listingText)
  );
}

/**
 * 商品库展示用短编号（优先 externalKey；不再展示本地 #id）。
 * @param {object} row
 * @returns {string}
 */
export function formatListingItemCode(row) {
  const key = String(row?.externalKey || '').trim();
  if (!key) return '—';
  if (key.length <= 18) return key;
  return `${key.slice(0, 8)}…${key.slice(-4)}`;
}

/** hover 完整编号信息（不含本地数字 id） */
export function formatListingItemCodeTitle(row) {
  const key = String(row?.externalKey || '').trim();
  return key || '';
}

/** 商品库排序/展示用时间：优先进入待上品时间，否则采集 createdAt */
export function resolveListingCreatedAt(row) {
  if (!row || typeof row !== 'object') return '';
  return row.pendingListingAt || row.pending_listing_at || row.createdAt || row.created_at || '';
}

export function compareListingItemsByCreatedAtDesc(a, b) {
  const timeA = resolveListingCreatedAt(a);
  const timeB = resolveListingCreatedAt(b);
  if (!timeA && !timeB) return (Number(b?.id) || 0) - (Number(a?.id) || 0);
  if (!timeA) return 1;
  if (!timeB) return -1;
  const msA = Date.parse(timeA);
  const msB = Date.parse(timeB);
  const validA = Number.isFinite(msA);
  const validB = Number.isFinite(msB);
  if (!validA && !validB) return (Number(b?.id) || 0) - (Number(a?.id) || 0);
  if (!validA) return 1;
  if (!validB) return -1;
  if (msB !== msA) return msB - msA;
  return (Number(b?.id) || 0) - (Number(a?.id) || 0);
}

/** 插件/API 返回字段 → 前端统一结构 */
export function normalizeSourcingListingItem(row) {
  if (!row || typeof row !== 'object') return row;
  let imageUrls = row.imageUrls ?? row.image_urls;
  if (typeof imageUrls === 'string') {
    try {
      const parsed = JSON.parse(imageUrls);
      imageUrls = Array.isArray(parsed) ? parsed : [];
    } catch {
      imageUrls = [];
    }
  }
  if (!Array.isArray(imageUrls)) imageUrls = [];
  let printImageUrls = row.printImageUrls ?? row.print_image_urls;
  if (typeof printImageUrls === 'string') {
    try {
      const parsed = JSON.parse(printImageUrls);
      printImageUrls = Array.isArray(parsed) ? parsed : [];
    } catch {
      printImageUrls = [];
    }
  }
  if (!Array.isArray(printImageUrls)) printImageUrls = [];
  let detailImageUrls = row.detailImageUrls ?? row.detail_image_urls;
  if (typeof detailImageUrls === 'string') {
    try {
      const parsed = JSON.parse(detailImageUrls);
      detailImageUrls = Array.isArray(parsed) ? parsed : [];
    } catch {
      detailImageUrls = [];
    }
  }
  if (!Array.isArray(detailImageUrls)) detailImageUrls = [];
  if (detailImageUrls.length === 0) {
    detailImageUrls = extractUrlListFromRawFields(row, 'detailImageUrls', 'detail_image_urls');
  }
  if (printImageUrls.length === 0) {
    const fromRawPrint = extractUrlListFromRawFields(row, 'printImageUrls', 'print_image_urls');
    if (fromRawPrint.length > 0) printImageUrls = fromRawPrint;
  }

  let imageAssets = row.imageAssets ?? row.image_assets;
  if (!imageAssets && row.raw && typeof row.raw === 'object') {
    imageAssets = row.raw.imageAssets || row.raw.image_assets;
  }
  imageAssets = normalizeImageAssetList(imageAssets);
  if (imageAssets.length > 0) {
    const assetCarousel = collectCarouselUrlsFromImageAssets(imageAssets);
    const assetPrints = collectPrintUrlsFromImageAssets(imageAssets);
    const assetDetails = collectDetailUrlsFromImageAssets(imageAssets);
    if (assetCarousel.length > 0) imageUrls = assetCarousel;
    if (assetPrints.length > 0) printImageUrls = assetPrints;
    if (assetDetails.length > 0) detailImageUrls = assetDetails;
  }

  const colorSlots = extractArrayField(row, 'colorSlots', 'color_slots');
  const printImageNames = extractArrayField(row, 'printImageNames', 'print_image_names');
  const mainImageNames = extractArrayField(row, 'mainImageNames', 'main_image_names');

  return {
    ...row,
    batchKey: row.batchKey ?? row.batch_key ?? '',
    imageUrls,
    printImageUrls,
    printImageNames,
    mainImageNames,
    detailImageUrls,
    colorSlots,
    imageAssets,
    externalKey: row.externalKey ?? row.external_key ?? '',
    listingText: row.listingText ?? row.listing_text ?? '',
    primaryImageUrl: row.primaryImageUrl ?? row.primary_image_url ?? '',
    remotePrimaryImageUrl: row.remotePrimaryImageUrl ?? row.remote_primary_image_url ?? '',
    batchPlatformKey: row.batchPlatformKey ?? row.batch_platform_key ?? '',
    ownerUserId: row.ownerUserId ?? row.owner_user_id ?? null,
    targetShopId: row.targetShopId ?? row.target_shop_id
      ?? row.targetPlatformShopId ?? row.target_platform_shop_id
      ?? null,
    listingCategoryPathLabel: row.listingCategoryPathLabel
      ?? row.listing_category_path_label
      ?? '',
    listingProductTemplateId: row.listingProductTemplateId
      ?? row.listing_product_template_id
      ?? '',
    listingProductTemplateName: row.listingProductTemplateName
      ?? row.listing_product_template_name
      ?? '',
    pendingListingAt: row.pendingListingAt ?? row.pending_listing_at ?? null,
    createdAt: resolveListingCreatedAt(row) || row.createdAt || row.created_at || '',
  };
}

/** 列表/批次聚合内存：去掉大字段，避免万条级 rawJsonSnippet 撑爆内存 */
export function stripListingItemForListStorage(item) {
  if (!item || typeof item !== 'object') return item;
  const {
    rawJsonSnippet: _rawJsonSnippet,
    raw_json_snippet: _rawJsonSnake,
    productMetadata: _productMetadata,
    product_metadata: _productMetadataSnake,
    ...rest
  } = item;
  let raw = rest.raw;
  if (raw && typeof raw === 'object') {
    const {
      productMetadata: _pm,
      product_metadata: _pmd,
      ...rawLight
    } = raw;
    raw = Object.keys(rawLight).length > 0 ? rawLight : undefined;
  }
  return raw !== undefined ? { ...rest, raw } : rest;
}

/**
 * 主图候选 URL 列表（历史轮播池；展示时仅取第 1 张作主图，其余并入详情图）。
 * @param {object} row
 * @returns {string[]}
 */
function listMainImageCandidateUrls(row) {
  if (!row || typeof row !== 'object') return [];

  const preferred = getListingPreferredOverrides(row.id);
  if (preferred?.hasMainOverride) {
    return uniqueUrlList(preferred.mainImageUrls, { limit: 40 });
  }
  if (preferred?.detailImageUrls?.length) {
    // 仅有 POD 时用详情图顶上主图候选
    return uniqueUrlList(preferred.detailImageUrls, { limit: 40 });
  }

  const assets = normalizeImageAssetList(
    row.imageAssets
    || row.raw?.imageAssets
    || row.raw?.image_assets
    || [],
  );
  if (assets.length > 0) {
    const fromAssets = collectCarouselUrlsFromImageAssets(assets);
    if (fromAssets.length > 0) return uniqueUrlList(fromAssets, { limit: 40 });
    const primary = row.primaryImageUrl || row.remotePrimaryImageUrl;
    return primary ? [String(primary).trim()].filter(Boolean) : [];
  }

  const primary = row.primaryImageUrl || row.remotePrimaryImageUrl;
  if (isGoodsCompositeListingRow(row) && primary) {
    return [String(primary).trim()].filter(Boolean);
  }

  const out = [];
  if (primary) out.push(String(primary).trim());
  const extras = Array.isArray(row.imageUrls) ? row.imageUrls : [];
  for (const u of extras) {
    if (u && typeof u === 'string' && !out.some((x) => urlsLooselySame(x, u))) {
      out.push(u);
    }
  }
  if (out.length === 0) {
    const colorSlots = resolveListingColorSlots(row);
    for (const slot of (Array.isArray(colorSlots) ? colorSlots : [])) {
      const mainUrl = String(slot?.mainUrl || slot?.main_url || '').trim();
      if (mainUrl && !out.some((x) => urlsLooselySame(x, mainUrl))) {
        out.push(mainUrl);
      }
    }
  }
  return out.slice(0, 40);
}

/**
 * 显式详情图 URL（不含「主图候选其余张」的合并；由 detailImageUrlsForListingItem 再拼）。
 * @param {object} row
 * @returns {string[]}
 */
function listExplicitDetailImageUrls(row) {
  if (!row || typeof row !== 'object') return [];

  const preferred = getListingPreferredOverrides(row.id);
  // 显式覆盖（含空数组）：手动删光详情图后不得再回退到行上旧字段
  if (preferred?.hasDetailOverride) {
    return uniqueUrlList(preferred.detailImageUrls, { limit: 40 });
  }
  // 仅有 preferred 主图、无详情覆盖时，仍读行上原始详情字段
  if (preferred?.hasMainOverride || preferred?.mainImageUrls?.length) {
    const fromRow = [
      ...(Array.isArray(row.detailImageUrls) ? row.detailImageUrls : []),
      ...(Array.isArray(row.detail_image_urls) ? row.detail_image_urls : []),
      ...extractUrlListFromRawFields(row, 'detailImageUrls', 'detail_image_urls'),
    ];
    if (fromRow.length > 0) return uniqueUrlList(fromRow, { limit: 40 });
  }

  const assets = normalizeImageAssetList(
    row.imageAssets
    || row.raw?.imageAssets
    || row.raw?.image_assets
    || [],
  );
  if (assets.length > 0) {
    const fromAssets = collectDetailUrlsFromImageAssets(assets);
    if (fromAssets.length > 0) return uniqueUrlList(fromAssets, { limit: 40 });
    // 无 detail 角色时：主图候选的其余张仍可走 listMainImageCandidateUrls 并入
    return [];
  }

  const candidates = [
    ...(Array.isArray(row.detailImageUrls) ? row.detailImageUrls : []),
    ...(Array.isArray(row.detail_image_urls) ? row.detail_image_urls : []),
    ...extractUrlListFromRawFields(row, 'detailImageUrls', 'detail_image_urls'),
  ];
  return uniqueUrlList(candidates, { limit: 40 });
}

/**
 * 主图 URL 列表（仅 1 张）。
 * 方案三：有 imageAssets 时优先 composite；无主图时 detail 可顶上。
 * @param {object} row 商品库条目
 * @returns {string[]}
 */
export function carouselUrlsForListingItem(row) {
  const main = listMainImageCandidateUrls(row)[0];
  return main ? [main] : [];
}

/**
 * 商品详情「详情图」区：主图 + 详情图全部（主图在前，去重）。
 * @param {object} row
 * @returns {string[]}
 */
export function allListingGalleryUrlsForItem(row) {
  if (!row || typeof row !== 'object') return [];
  const preferred = getListingPreferredOverrides(row.id);
  const mains = carouselUrlsForListingItem(row);
  const details = detailImageUrlsForListingItem(row);
  const seen = new Set();
  const out = [];
  const push = (u) => {
    const url = String(u || '').trim();
    if (!url) return;
    const key = uploadsPathKey(url) || url;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(url);
  };
  mains.forEach(push);
  details.forEach(push);
  if (out.length === 0) {
    push(preferred?.primaryImageUrl || row.primaryImageUrl || row.remotePrimaryImageUrl);
  }
  return out.slice(0, 40);
}

  /**
   * 详情图 URL 列表：显式详情图 + 主图候选中除首张外的其余图片。
   * 若已有 colorSlots，则只取各色槽细节图，禁止把其他颜色主图混进细节列。
   * @param {object} row
   * @returns {string[]}
   */
  export function detailImageUrlsForListingItem(row) {
  if (!row || typeof row !== 'object') return [];
  const preferred = getListingPreferredOverrides(row.id);
  const colorSlots = resolveListingColorSlots(row);
  if (Array.isArray(colorSlots) && colorSlots.length > 0) {
    const fromSlots = colorSlots.flatMap((slot) => detailUrlsFromListingColorSlot(slot));
    return uniqueUrlList(fromSlots, { limit: 40 });
  }
  if (preferred?.hasDetailOverride) {
    return uniqueUrlList(preferred.detailImageUrls, { limit: 40 });
  }
  const candidates = listMainImageCandidateUrls(row);
  const mainUrl = candidates[0] || '';
  const leftoverMains = candidates.slice(1);
  const explicit = listExplicitDetailImageUrls(row);
  const mainIsExplicitDetail = Boolean(
    mainUrl && explicit.some((u) => urlsLooselySame(u, mainUrl)),
  );
  return uniqueUrlList([...leftoverMains, ...explicit], {
    // 仅排除「只占主图位、并非显式详情」的当前主图，避免详情列被顶上逻辑掏空
    excludeUrl: mainIsExplicitDetail ? '' : mainUrl,
    limit: 40,
  });
}

/**
 * 印花图 URL 列表（手动覆盖 / imageAssets.role=print / printImageUrls）。
 * @param {object} row
 * @returns {string[]}
 */
export function printImageUrlsForListingItem(row) {
  if (!row || typeof row !== 'object') return [];

  const preferred = getListingPreferredOverrides(row.id);
  // 显式覆盖（含空数组）：手动删光印花后不得再回退到行上旧字段
  if (preferred?.hasPrintOverride) {
    return uniqueUrlList(preferred.printImageUrls, { limit: 40 });
  }

  const assets = normalizeImageAssetList(
    row.imageAssets
    || row.raw?.imageAssets
    || row.raw?.image_assets
    || [],
  );
  if (assets.length > 0) {
    const fromAssets = collectPrintUrlsFromImageAssets(assets);
    if (fromAssets.length > 0) return uniqueUrlList(fromAssets, { limit: 40 });
  }

  const explicit = [
    ...(Array.isArray(row.printImageUrls) ? row.printImageUrls : []),
    ...(Array.isArray(row.print_image_urls) ? row.print_image_urls : []),
    ...extractUrlListFromRawFields(row, 'printImageUrls', 'print_image_urls'),
  ];
  const cleaned = uniqueUrlList(explicit, { limit: 40 });
  if (cleaned.length > 0) return cleaned;

  return [];
}

function stripImageFileExtension(name) {
  return String(name || '').replace(/\.[a-zA-Z0-9]{1,8}$/i, '').trim();
}

function fileNameFromUrl(url) {
  const raw = String(url || '').trim();
  if (!raw) return '';
  try {
    const path = new URL(raw, 'https://local.invalid').pathname;
    return decodeURIComponent((path.split('/').pop() || '').split('?')[0]);
  } catch {
    return (raw.split('/').pop() || '').split('?')[0];
  }
}

/**
 * 存储对象键式文件名（如 32 位 hex、img-{hash}），不是图片空间展示名（如 upload-1786…）。
 * 货号/SKU 拼接与错误日志展示时不应使用这类弱名。
 */
export function isOpaqueStorageBaseName(name) {
  const raw = String(name || '').trim();
  if (!raw) return true;
  const base = stripImageFileExtension(raw);
  if (!base) return true;
  // 纯内容哈希 / UUID（无 upload- 等人读前缀）
  if (/^[a-f0-9]{32}$/i.test(base)) return true;
  if (/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(base)) return true;
  if (/^[a-f0-9]{40}$/i.test(base)) return true;
  // 图片空间落库常用的存储叶子名：img-{32hex}
  if (/^img-[a-f0-9]{32}$/i.test(base)) return true;
  if (/^img-[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(base)) return true;
  return false;
}

/** 系统占位名（印花1 / 主图1 / 细节(1) 等），不应覆盖图片原名 */
export function isGenericListingImageName(name) {
  const base = stripImageFileExtension(name);
  if (!base) return true;
  if (/^image-\d+$/i.test(base)) return true;
  if (/^未命名(主图|细节图?|印花图?)?$/u.test(base)) return true;
  return /^(印花|主图|细节图?|详情图?|图片)\s*\(?\d+\)?$/u.test(base);
}

function preferReadablePrintName(...candidates) {
  for (const raw of candidates) {
    const name = stripImageFileExtension(raw);
    if (name && !isOpaqueStorageBaseName(name) && !isGenericListingImageName(name)) return name;
  }
  return '';
}

/**
 * 商品库印花图展示名（去扩展名）；多张按 URL 顺序，供货号/SKU 拼接。
 * @param {object} row
 * @returns {string[]}
 */
export function printImageNamesForListingItem(row) {
  if (!row || typeof row !== 'object') return [];

  const urls = printImageUrlsForListingItem(row);
  const assets = normalizeImageAssetList(
    row.imageAssets
    || row.raw?.imageAssets
    || row.raw?.image_assets
    || [],
  ).filter((a) => a.role === IMAGE_ASSET_ROLES.PRINT);

  const preferred = getListingPreferredOverrides(row.id);
  const preferredNames = Array.isArray(preferred?.printImageNames)
    ? preferred.printImageNames.map((n) => stripImageFileExtension(n)).filter(Boolean)
    : [];

  const rawPrintNames = Array.isArray(row?.raw?.printImageNames)
    ? row.raw.printImageNames
    : (Array.isArray(row?.raw?.print_image_names) ? row.raw.print_image_names : []);
  // 保持与 urls 下标对齐，勿先 filter 掉占位名（否则会错位）
  const rowPrintNames = [
    ...(Array.isArray(row.printImageNames) ? row.printImageNames : []),
    ...(Array.isArray(row.print_image_names) ? row.print_image_names : []),
    ...rawPrintNames,
  ].map((n) => stripImageFileExtension(n));

  if (urls.length === 0) {
    return [...preferredNames, ...rowPrintNames]
      .filter((n) => n && !isOpaqueStorageBaseName(n) && !isGenericListingImageName(n));
  }

  return urls.map((url, index) => {
    const asset = assets.find((a) => a.url && urlsLooselySame(a.url, url));
    const fromUrl = stripImageFileExtension(fileNameFromUrl(url));
    const readable = preferReadablePrintName(
      asset?.name,
      preferredNames[index],
      rowPrintNames[index],
      // URL 文件名仅在非哈希时可用；哈希留给上层回表图片空间
      isOpaqueStorageBaseName(fromUrl) ? '' : fromUrl,
    );
    return readable || `印花${index + 1}`;
  }).filter(Boolean);
}

/**
 * 在以「-」结尾的货号/SKU 后拼接印花名；多张用 + 连接。
 * 若已拼接过，会从第一个「-」前缀段起重写后缀，避免重复叠加。
 * @param {string} code
 * @param {string[]} printNames
 * @param {{ maxLen?: number }} [opts]
 * @returns {string}
 */
export function appendPrintNamesAfterHyphen(code, printNames, opts = {}) {
  const maxLen = Number(opts.maxLen) > 0 ? Number(opts.maxLen) : SHEIN_SUPPLIER_CODE_MAX_LEN;
  const joined = (Array.isArray(printNames) ? printNames : [])
    .map((n) => stripImageFileExtension(n))
    .filter(Boolean)
    .join('+');
  const raw = String(code || '').trim();
  if (!joined) return raw;
  if (!raw) return joined.slice(0, maxLen);

  let prefix = raw;
  if (!raw.endsWith('-')) {
    const m = raw.match(/^(.+-)/);
    prefix = m ? m[1] : `${raw}-`;
  }
  const room = Math.max(0, maxLen - prefix.length);
  return `${prefix}${joined.slice(0, room)}`;
}

/** 第三方刊登桥接产生的条目，不应出现在本地商品库列表 */
export function isThirdPartyOnlyListingItem(item) {
  const raw = item?.raw && typeof item.raw === 'object'
    ? item.raw
    : (() => {
      try {
        const parsed = JSON.parse(item?.rawJsonSnippet || '');
        return parsed && typeof parsed === 'object' ? parsed : {};
      } catch {
        return {};
      }
    })();
  const source = String(raw?.source || item?.source || '').trim();
  return source === 'thirdPartyReceive' || source === 'open_receive_auto_listing';
}

export function resolveListingItemSourceLabel(item) {
  const bridgeLabel = resolveListingBridgeSourceLabelFromItem(item);
  if (bridgeLabel) return bridgeLabel;

  const pk = String(item?.batchPlatformKey || '').trim();
  if (pk === 'ipe-goods-composite') return i18n.t('workbench:listingPublish.sourceGoodsComposite');
  if (pk === 'ipe-workflow-listing') return i18n.t('workbench:listingPublish.sourceWorkflow');
  if (pk === 'ipe-detail-bridge-listing') return i18n.t('workbench:listingPublish.sourceDetailBridge');
  if (pk === 'ipe-manual-listing') return i18n.t('workbench:listingPublish.sourceManual');
  const rawSource = String(item?.raw?.source || item?.source || '').trim();
  if (rawSource === 'manual_create') return i18n.t('workbench:listingPublish.sourceManual');
  if (rawSource === 'thirdPartyReceive') return i18n.t('workbench:listingPublish.sourceThirdParty');
  if (rawSource === 'cropDetail') return i18n.t('workbench:listingPublish.sourceCrop');
  return i18n.t('workbench:listingPublish.sourcePlugin');
}

/**
 * 单条商品库条目的细节图张数（多色按「单色最大」计，禁止跨色扁平累加误判）。
 * @param {object} row
 * @returns {number}
 */
export function listingItemMaxDetailImageCount(row) {
  if (!row || typeof row !== 'object') return 0;
  const colorSlots = resolveListingColorSlots(row);
  if (Array.isArray(colorSlots) && colorSlots.length > 0) {
    return colorSlots.reduce((max, slot) => {
      const count = detailUrlsFromListingColorSlot(slot).length;
      return count > max ? count : max;
    }, 0);
  }
  return detailImageUrlsForListingItem(row).length;
}

/**
 * 找出细节图超过平台发品上限的商品库条目（提交刊登前预检，按色拦截不截断）。
 * @param {object[]} items
 * @param {number} [max=SHEIN_MAX_DETAIL_IMAGES]
 * @returns {{ id: *, title: string, count: number, max: number }[]}
 */
export function findListingItemsExceedingDetailImageLimit(
  items,
  max = SHEIN_MAX_DETAIL_IMAGES,
) {
  const limit = Number(max);
  const safeMax = Number.isFinite(limit) && limit > 0 ? limit : SHEIN_MAX_DETAIL_IMAGES;
  return (Array.isArray(items) ? items : [])
    .filter((row) => row != null)
    .map((row) => {
      const count = listingItemMaxDetailImageCount(row);
      return {
        id: row.id,
        title: resolveListingItemDisplayTitle(row)
          || String(row.title || '').trim()
          || i18n.t('workbench:listingLimits.detailItemFallback', { id: row.id }),
        count,
        max: safeMax,
      };
    })
    .filter((row) => row.count > row.max);
}

/**
 * @param {{ title: string, count: number, max: number }[]} violations
 * @param {string} [platformLabel='SHEIN']
 * @returns {string}
 */
export function formatDetailImageLimitError(violations, platformLabel = 'SHEIN') {
  const list = Array.isArray(violations) ? violations.filter(Boolean) : [];
  if (list.length === 0) return '';
  const max = list[0]?.max ?? SHEIN_MAX_DETAIL_IMAGES;
  const label = String(platformLabel || 'SHEIN').trim() || 'SHEIN';
  const samples = list
    .slice(0, 3)
    .map((v) => i18n.t('workbench:listingLimits.detailSample', { title: v.title, count: v.count }))
    .join(i18n.language && i18n.language.startsWith('zh') ? '、' : ', ');
  const more = list.length > 3
    ? i18n.t('workbench:listingLimits.detailMore', { count: list.length })
    : '';
  return i18n.t('workbench:listingLimits.detailBatchError', { label, max, samples, more });
}
